import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import {   authService } from '../api/auth.js';
import { Cog6ToothIcon, PlusIcon, PencilIcon, XMarkIcon, CheckIcon, UserIcon, ChartBarIcon, SparklesIcon, EyeIcon, EyeSlashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { isDemoUser, solicitarRegistroDemo } from '../utils/demoMode';

const AdminPanel = ({ usuario, onClose }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrModalNuevoUsuario, setMostrModalNuevoUsuario] = useState(false);
  const [mostrModalEditarUsuario, setMostrModalEditarUsuario] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    nombre_completo: '',
    contraseña: '',
    confirmarContraseña: '',
    rol: 'operario'
  });
  const [formEditData, setFormEditData] = useState({
    email: '',
    nombre_completo: '',
    contraseña: '',
    confirmarContraseña: ''
  });
  const [creandoUsuario, setCreandoUsuario] = useState(false);
  const [editandoUsuario, setEditandoUsuario] = useState(false);
  const [mostrarContraseñaNuevo, setMostrarContraseñaNuevo] = useState(false);
  const [mostrarConfirmarContraseñaNuevo, setMostrarConfirmarContraseñaNuevo] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    cargarUsuarios();
  }, []);

  // Función auxiliar para hacer requests con renovación automática de token
  const fetchConRefresh = async (url, options = {}) => {
    try {
      const headersAuth = authService.obtenerHeaders();
      if (options.body instanceof FormData) {
        delete headersAuth['Content-Type'];
      }

      let response = await fetch(url, {
        ...options,
        headers: {
          ...headersAuth,
          ...options.headers
        }
      });

      // Si recibe 401, intentar renovar el token y reintentar
      if (response.status === 401) {
        console.warn('⚠️ Token expirado, intentando renovar...');
        try {
          await authService.renovarToken();
          // Reintentar con el nuevo token
          const retryHeadersAuth = authService.obtenerHeaders();
          if (options.body instanceof FormData) {
            delete retryHeadersAuth['Content-Type'];
          }

          response = await fetch(url, {
            ...options,
            headers: {
              ...retryHeadersAuth,
              ...options.headers
            }
          });
        } catch (refreshError) {
          console.error('❌ No se pudo renovar el token:', refreshError);
          Swal.fire({
            icon: 'warning',
            title: 'Sesión expirada',
            text: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
            confirmButtonText: 'Ir a Login'
          }).then(() => {
            // Redirigir a login
            window.location.href = '/';
          });
          throw new Error('Sesión expirada');
        }
      }

      return response;
    } catch (error) {
      console.error('Error en request:', error);
      throw error;
    }
  };

  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      const response = await fetchConRefresh(`${API_URL}/api/auth/usuarios`, {
        method: 'GET'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al cargar usuarios');
      }

      const data = await response.json();
      const usuariosMismaOptica = data.filter((usr) => usr.optica_id === usuario?.optica_id);
      setUsuarios(usuariosMismaOptica);
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada') {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los usuarios'
        });
      }
    } finally {
      setLoading(false);
    }
  };


  const cambiarRol = async (usuarioId, nuevoRol) => {
    if (isDemoUser(usuario) || isDemoUser(authService.obtenerUsuarioActual())) {
      solicitarRegistroDemo('modificar roles y permisos de los usuarios');
      return;
    }
    try {
      const response = await fetchConRefresh(`${API_URL}/api/auth/usuarios/${usuarioId}`, {
        method: 'PUT',
        body: JSON.stringify({
          rol: nuevoRol
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al actualizar rol');
      }

      Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: 'Rol actualizado correctamente',
        timer: 1500,
        showConfirmButton: false
      });

      cargarUsuarios();
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada') {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'No se pudo actualizar el rol'
        });
      }
    }
  };

  const desactivarUsuario = async (usuarioId) => {
    if (isDemoUser(usuario) || isDemoUser(authService.obtenerUsuarioActual())) {
      solicitarRegistroDemo('desactivar cuentas de usuario');
      return;
    }
    const resultado = await Swal.fire({
      title: '¿Desactivar usuario?',
      text: 'Esta acción desactivará la cuenta del usuario',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, desactivar',
      cancelButtonText: 'Cancelar'
    });

    if (resultado.isConfirmed) {
      try {
        const response = await fetchConRefresh(`${API_URL}/api/auth/usuarios/${usuarioId}`, {
          method: 'DELETE'
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Error al desactivar usuario');
        }

        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Usuario desactivado',
          timer: 1500,
          showConfirmButton: false
        });

        cargarUsuarios();
      } catch (error) {
        console.error('Error:', error);
        if (error.message !== 'Sesión expirada') {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.message || 'No se pudo desactivar el usuario'
          });
        }
      }
    }
  };

  const activarUsuario = async (usuarioId, nombreUsuario) => {
    if (isDemoUser(usuario) || isDemoUser(authService.obtenerUsuarioActual())) {
      solicitarRegistroDemo('activar o reactivar usuarios');
      return;
    }
    const resultado = await Swal.fire({
      title: '¿Activar usuario?',
      text: `Esto reactivará la cuenta de ${nombreUsuario}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, activar',
      cancelButtonText: 'Cancelar'
    });

    if (resultado.isConfirmed) {
      try {
        const response = await fetchConRefresh(`${API_URL}/api/auth/usuarios/${usuarioId}/activar`, {
          method: 'PUT'
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Error al activar usuario');
        }

        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Usuario activado correctamente',
          timer: 1500,
          showConfirmButton: false
        });

        cargarUsuarios();
      } catch (error) {
        console.error('Error:', error);
        if (error.message !== 'Sesión expirada') {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.message || 'No se pudo activar el usuario'
          });
        }
      }
    }
  };

  const crearNuevoUsuario = async (e) => {
    e.preventDefault();
    
    if (isDemoUser(usuario) || isDemoUser(authService.obtenerUsuarioActual())) {
      setMostrModalNuevoUsuario(false);
      solicitarRegistroDemo('crear y administrar nuevos usuarios de tu equipo');
      return;
    }
    
    // Validaciones
    if (!formData.email || !formData.nombre_completo || !formData.contraseña || !formData.confirmarContraseña) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos requeridos',
        text: 'Por favor completa todos los campos'
      });
      return;
    }

    if (formData.contraseña.length < 6) {
      Swal.fire({
        icon: 'warning',
        title: 'Contraseña débil',
        text: 'La contraseña debe tener al menos 6 caracteres'
      });
      return;
    }

    if (formData.contraseña !== formData.confirmarContraseña) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Las contraseñas no coinciden'
      });
      return;
    }

    try {
      setCreandoUsuario(true);
      const payload = {
        email: formData.email,
        nombre_completo: formData.nombre_completo,
        contraseña: formData.contraseña,
        rol: formData.rol,
        optica_id: usuario?.optica_id
      };

      const response = await fetchConRefresh(`${API_URL}/api/auth/usuarios`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al crear usuario');
      }

      Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: 'Usuario creado correctamente',
        timer: 1500,
        showConfirmButton: false
      });

      // Limpiar formulario y cerrar modal
      setFormData({
        email: '',
        nombre_completo: '',
        contraseña: '',
        confirmarContraseña: '',
        rol: 'operario'
      });
      setMostrarContraseñaNuevo(false);
      setMostrarConfirmarContraseñaNuevo(false);
      setMostrModalNuevoUsuario(false);
      cargarUsuarios();
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No se pudo crear el usuario'
      });
    } finally {
      setCreandoUsuario(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setFormEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const abrirModalEditar = (usr) => {
    setUsuarioEditando(usr);
    setFormEditData({
      email: usr.email,
      nombre_completo: usr.nombre_completo,
      contraseña: '',
      confirmarContraseña: ''
    });
    setMostrModalEditarUsuario(true);
  };

  const editarUsuario = async (e) => {
    e.preventDefault();

    if (isDemoUser(usuario) || isDemoUser(authService.obtenerUsuarioActual())) {
      setMostrModalEditarUsuario(false);
      solicitarRegistroDemo('modificar usuarios de tu equipo');
      return;
    }
    
    // Validaciones
    if (!formEditData.email || !formEditData.nombre_completo) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos requeridos',
        text: 'El email y el nombre completo son obligatorios'
      });
      return;
    }

    // Si hay contraseña, validar que coincidan
    if (formEditData.contraseña) {
      if (formEditData.contraseña.length < 6) {
        Swal.fire({
          icon: 'warning',
          title: 'Contraseña débil',
          text: 'La contraseña debe tener al menos 6 caracteres'
        });
        return;
      }

      if (formEditData.contraseña !== formEditData.confirmarContraseña) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Las contraseñas no coinciden'
        });
        return;
      }
    }

    try {
      setEditandoUsuario(true);
      
      // Preparar datos para enviar (solo campos que han cambiado)
      const updateData = {};
      
      if (formEditData.email !== usuarioEditando.email) {
        updateData.email = formEditData.email;
      }
      
      if (formEditData.nombre_completo !== usuarioEditando.nombre_completo) {
        updateData.nombre_completo = formEditData.nombre_completo;
      }
      
      if (formEditData.contraseña) {
        if (usuarioEditando?.email?.toLowerCase() === 'usuarioprueba@optica.com' && usuario?.rol !== 'superadmin') {
          // No permitir cambiar contraseña de demo salvo superadmin
        } else {
          updateData.contraseña = formEditData.contraseña;
        }
      }

      const response = await fetchConRefresh(`${API_URL}/api/auth/usuarios/${usuarioEditando.id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al actualizar usuario');
      }

      Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: 'Usuario actualizado correctamente',
        timer: 1500,
        showConfirmButton: false
      });

      // Limpiar y cerrar modal
      setFormEditData({
        email: '',
        nombre_completo: '',
        contraseña: '',
        confirmarContraseña: ''
      });
      setUsuarioEditando(null);
      setMostrModalEditarUsuario(false);
      cargarUsuarios();
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada') {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'No se pudo actualizar el usuario'
        });
      }
    } finally {
      setEditandoUsuario(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[1050] flex items-start justify-center pt-16 sm:pt-20 pb-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-6xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Cog6ToothIcon className="h-6 w-6" />
            Panel de Administración
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 rounded-full p-2 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Gestión de Usuarios</h3>
              <button
                onClick={() => {
                  if (isDemoUser(usuario) || isDemoUser(authService.obtenerUsuarioActual())) {
                    solicitarRegistroDemo('crear y registrar nuevos usuarios para tu óptica');
                    return;
                  }
                  setMostrModalNuevoUsuario(true);
                }}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium cursor-pointer"
              >
                <PlusIcon className="h-5 w-5" />
                Agregar Usuario
              </button>
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Cargando usuarios...</p>
              </div>
            ) : usuarios.length === 0 ? (
              <p className="text-gray-500">No hay usuarios</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Usuario</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Email</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Estado</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Rol</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Asignar Rol</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {usuarios.map((usr) => (
                      <tr key={usr.id} className={`transition-colors ${usr.activo ? 'hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}`}>
                        <td className="px-4 py-3">
                          <div className={`font-medium ${usr.activo ? 'text-gray-900' : 'text-gray-400'}`}>
                            {usr.nombre_completo}
                          </div>
                        </td>
                        <td className={`px-4 py-3 text-sm ${usr.activo ? 'text-gray-600' : 'text-gray-400'}`}>
                          {usr.email}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                            usr.activo 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {usr.activo ? (
                              <><CheckIcon className="h-3 w-3" /> Activo</>
                            ) : (
                              <><XMarkIcon className="h-3 w-3" /> Inactivo</>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            usr.rol === 'admin' ? 'bg-red-100 text-red-800' :
                            usr.rol === 'gerente' ? 'bg-purple-100 text-purple-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {usr.rol.charAt(0).toUpperCase() + usr.rol.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={usr.rol}
                            onChange={(e) => cambiarRol(usr.id, e.target.value)}
                            disabled={!usr.activo || usr.id === usuario.id || (usr.rol === 'superadmin' && usuario?.rol !== 'superadmin')}
                            className={`px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              !usr.activo || usr.id === usuario.id || (usr.rol === 'superadmin' && usuario?.rol !== 'superadmin') ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                          >
                            <option value="admin">Admin</option>
                            <option value="gerente">Gerente</option>
                            <option value="operario">Operario</option>
                            {usuario?.rol === 'superadmin' && <option value="superadmin">Superadmin</option>}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {usr.activo && (usr.rol !== 'superadmin' || usr.id === usuario.id) && (
                              <button
                                onClick={() => abrirModalEditar(usr)}
                                className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors flex items-center gap-1"
                              >
                                <PencilIcon className="h-4 w-4" /> Editar
                              </button>
                            )}
                            {usr.activo ? (
                              usr.id !== usuario.id && usr.rol !== 'superadmin' && (
                                <button
                                  onClick={() => desactivarUsuario(usr.id)}
                                  className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors"
                                >
                                  Desactivar
                                </button>
                              )
                            ) : (
                              <button
                                onClick={() => activarUsuario(usr.id, usr.nombre_completo)}
                                className="text-green-600 hover:text-green-800 font-medium text-sm transition-colors"
                              >
                                Activar
                              </button>
                            )}
                            {usr.id === usuario.id && usr.activo && (
                              <span className="text-gray-400 text-sm">(Tu cuenta)</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Información de Roles</h3>
            <div className="space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="font-medium text-red-900 mb-1 flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5" /> Admin
                </p>
                <p className="text-sm text-red-700">Acceso completo al sistema. Puede gestionar usuarios y roles.</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="font-medium text-purple-900 mb-1 flex items-center gap-2">
                  <ChartBarIcon className="h-5 w-5" /> Gerente
                </p>
                <p className="text-sm text-purple-700">Acceso a reportes y gestión de datos. Permisos limitados.</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-medium text-blue-900 mb-1 flex items-center gap-2">
                  <UserIcon className="h-5 w-5" /> Operario
                </p>
                <p className="text-sm text-blue-700">Acceso básico. Puede ver y registrar información.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal para Nuevo Usuario */}
      {mostrModalNuevoUsuario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-60 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <PlusIcon className="h-6 w-6" />
                Agregar Nuevo Usuario
              </h3>
              <button
                onClick={() => setMostrModalNuevoUsuario(false)}
                className="text-white hover:bg-green-800 rounded-full p-2 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={crearNuevoUsuario} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="usuario@optica.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre Completo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nombre_completo"
                  value={formData.nombre_completo}
                  onChange={handleInputChange}
                  placeholder="Juan Pérez"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={mostrarContraseñaNuevo ? 'text' : 'password'}
                    name="contraseña"
                    value={formData.contraseña}
                    onChange={handleInputChange}
                    placeholder="Mínimo 6 caracteres"
                    className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarContraseñaNuevo((prev) => !prev)}
                    className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700"
                    aria-label={mostrarContraseñaNuevo ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {mostrarContraseñaNuevo ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar Contraseña <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={mostrarConfirmarContraseñaNuevo ? 'text' : 'password'}
                    name="confirmarContraseña"
                    value={formData.confirmarContraseña}
                    onChange={handleInputChange}
                    placeholder="Repite la contraseña"
                    className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarConfirmarContraseñaNuevo((prev) => !prev)}
                    className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700"
                    aria-label={mostrarConfirmarContraseñaNuevo ? 'Ocultar confirmación de contraseña' : 'Mostrar confirmación de contraseña'}
                  >
                    {mostrarConfirmarContraseñaNuevo ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rol <span className="text-red-500">*</span>
                </label>
                <select
                  name="rol"
                  value={formData.rol}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="operario">Operario</option>
                  <option value="gerente">Gerente</option>
                  <option value="admin">Admin</option>
                  {usuario?.rol === 'superadmin' && <option value="superadmin">Superadmin</option>}
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  <strong>Nota:</strong> El usuario se creará con el rol seleccionado y activo de forma inmediata.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={creandoUsuario}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {creandoUsuario ? 'Creando...' : 'Crear Usuario'}
                </button>
                <button
                  type="button"
                  onClick={() => setMostrModalNuevoUsuario(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para Editar Usuario */}
      {mostrModalEditarUsuario && usuarioEditando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-60 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <PencilIcon className="h-6 w-6" />
                Editar Usuario
              </h3>
              <button
                onClick={() => setMostrModalEditarUsuario(false)}
                className="text-white hover:bg-blue-800 rounded-full p-2 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={editarUsuario} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formEditData.email}
                  onChange={handleEditInputChange}
                  placeholder="usuario@optica.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre Completo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nombre_completo"
                  value={formEditData.nombre_completo}
                  onChange={handleEditInputChange}
                  placeholder="Juan Pérez"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {usuarioEditando?.email?.toLowerCase() === 'usuarioprueba@optica.com' && usuario?.rol !== 'superadmin' ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                  <ShieldCheckIcon className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-900 leading-relaxed">
                    <strong className="block font-bold mb-0.5">Cuenta de Demostración Protegida</strong>
                    La contraseña de este usuario oficial está protegida. Solamente un <strong>SuperAdmin</strong> tiene permisos para modificarla.
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nueva Contraseña
                    </label>
                    <input
                      type="password"
                      name="contraseña"
                      value={formEditData.contraseña}
                      onChange={handleEditInputChange}
                      placeholder="Dejar en blanco para mantener la actual"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres si deseas cambiarla</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar Nueva Contraseña
                    </label>
                    <input
                      type="password"
                      name="confirmarContraseña"
                      value={formEditData.confirmarContraseña}
                      onChange={handleEditInputChange}
                      placeholder="Confirmar nueva contraseña"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-700">
                      <strong>Nota:</strong> Si deseas cambiar la contraseña, debes completar ambos campos. Si no deseas cambiarla, déjalos en blanco.
                    </p>
                  </div>
                </>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={editandoUsuario}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {editandoUsuario ? 'Guardando...' : 'Guardar Cambios'}
                </button>
                <button
                  type="button"
                  onClick={() => setMostrModalEditarUsuario(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const usuarioShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  optica_id: PropTypes.number.isRequired,
  rol: PropTypes.string.isRequired,
});

AdminPanel.propTypes = {
  usuario: usuarioShape.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminPanel;
